// The SSO user profile travels between the ACT consoles as base64: in the
// ?info= query param from the Auth Portal, and in localStorage (user_info).
// It used to be written with btoa(JSON.stringify(...)), which only handles
// Latin-1 and throws for names such as "Łukasz" or "O’Brien" (SID-2225). In
// the token-refresh path that throw logged the user out (SID-2231).
//
// encodeUserInfo writes UTF-8. decodeUserInfo reads UTF-8 and falls back to
// Latin-1, so profiles written the old way (e.g. "José") still decode, and
// ASCII-only profiles encode exactly as before.

const CHUNK = 0x8000;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}

function base64ToText(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    // Not valid UTF-8: written by btoa as Latin-1 before SID-2225.
    return binary;
  }
}

/** Returns the base64 string for a profile object. */
export function encodeUserInfo(value: unknown): string {
  return bytesToBase64(new TextEncoder().encode(JSON.stringify(value)));
}

/**
 * Returns the parsed profile, or null if it's missing or can't be read. Also
 * accepts the double-encoded form (a JSON string holding the JSON) that some
 * portal links produce.
 */
export function decodeUserInfo<T extends object>(base64: string | null | undefined): T | null {
  if (!base64) return null;
  try {
    let value: unknown = JSON.parse(base64ToText(base64));
    if (typeof value === "string") value = JSON.parse(value);
    return value !== null && typeof value === "object" ? (value as T) : null;
  } catch {
    return null;
  }
}
